import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import {
  convertFromLv95ToUserCoordinate,
  convertFromUserToLv95Coordinate,
  type CoordinateSystemPreference,
} from 'utils/coordinateSystemManager';
import { buildCoordinateInputs, normalizeCoordinateInput, parseCoordinateNumber } from 'utils/coordinateInputHelper.js';
import type { BorderType, Lv95Coordinate } from './cadastralShared';

interface UseCadastralCoordinateInputsOptions {
  coordinates: Lv95Coordinate[];
  setCoordinates: Dispatch<SetStateAction<Lv95Coordinate[]>>;
  coordinateSystemPreference: CoordinateSystemPreference;
  editingPolygonType: BorderType | null;
  editDraftCoordinatesRef: { current: Partial<Record<BorderType, Lv95Coordinate[] | null>> };
}

const useCadastralCoordinateInputs = ({
  coordinates,
  setCoordinates,
  coordinateSystemPreference,
  editingPolygonType,
  editDraftCoordinatesRef,
}: UseCadastralCoordinateInputsOptions) => {
  const [coordinateInputs, setCoordinateInputs] = useState<Array<{ x: string; y: string }>>([]);
  const isInputSyncRef = useRef(false);

  const buildCoordinateInputsForView = () =>
    buildCoordinateInputs({
      coordinates: coordinates.map((coordinate) =>
        convertFromLv95ToUserCoordinate(coordinate, coordinateSystemPreference)
      ),
    });

  useEffect(() => {
    if (isInputSyncRef.current) {
      isInputSyncRef.current = false;
      return;
    }
    setCoordinateInputs(buildCoordinateInputsForView());
  }, [coordinates, coordinateSystemPreference, editingPolygonType]);

  useEffect(() => {
    isInputSyncRef.current = false;
    setCoordinateInputs(buildCoordinateInputsForView());
  }, [coordinateSystemPreference]);

  const handleCoordinateInputChange = (index: number, axis: 'x' | 'y', rawValue: string | number) => {
    const normalizedInput = normalizeCoordinateInput(rawValue);
    isInputSyncRef.current = true;
    setCoordinateInputs((prev) => {
      const next = [...prev];
      const current = next[index] || { x: '0', y: '0' };
      next[index] = { ...current, [axis]: normalizedInput };
      return next;
    });

    const numericValue = parseCoordinateNumber(normalizedInput);
    const displayedCoordinate = coordinateInputs[index] || buildCoordinateInputsForView()[index] || { x: '0', y: '0' };
    const nextDisplayedCoordinate = {
      x: axis === 'x' ? numericValue : parseCoordinateNumber(displayedCoordinate.x),
      y: axis === 'y' ? numericValue : parseCoordinateNumber(displayedCoordinate.y),
    };
    const nextCoordinates = [...coordinates];
    nextCoordinates[index] = convertFromUserToLv95Coordinate(nextDisplayedCoordinate, coordinateSystemPreference);
    setCoordinates(nextCoordinates);
    if (editingPolygonType) {
      editDraftCoordinatesRef.current[editingPolygonType] = nextCoordinates;
    }
  };

  return { coordinateInputs, handleCoordinateInputChange };
};

export default useCadastralCoordinateInputs;
